import Box from "@mui/material/Box";
import Alert from "@mui/material/Alert";
import CircularProgress from "@mui/material/CircularProgress";
import Typography from "@mui/material/Typography";
import type { NotificationFeedItem } from "@situate/contracts/runtime";
import type {
  NotificationFeedFilters,
  NotificationsFeedStatus,
} from "@/features/deployment-dashboard/notifications/hooks/use-notifications-feed";
import { AlertDetailsPanel } from "@/features/deployment-dashboard/notifications/alert-details-panel";
import { NotificationFiltersBar } from "@/features/deployment-dashboard/widgets/panels/notification-filters";
import { NotificationList } from "@/features/deployment-dashboard/widgets/panels/notification-list";
import { SectionHeading } from "@/shared/ui";
import { NotificationsIcon } from "@/shared/ui/console-icons";

interface NotificationsFeedViewProps {
  filters: NotificationFeedFilters;
  notifications: Parameters<typeof NotificationList>[0]["notifications"];
  status: NotificationsFeedStatus;
  error: Error | null;
  onFiltersChange: (filters: NotificationFeedFilters) => void;
  selectedAlertId: string | null;
  onSelectNotification: (notification: NotificationFeedItem) => void;
  onCloseAlert: () => void;
}

/**
 * Notifications page shell: heading, filter controls, and the deployment-scoped
 * Runtime API notification history.
 */
export function NotificationsFeedView({
  filters,
  notifications,
  status,
  error,
  onFiltersChange,
  selectedAlertId,
  onSelectNotification,
  onCloseAlert,
}: NotificationsFeedViewProps) {
  return (
    <Box sx={{ display: "flex", alignItems: "flex-start", minHeight: "100%" }}>
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          display: "flex",
          flexDirection: "column",
          gap: 2.5,
        }}
      >
        <SectionHeading icon={<NotificationsIcon sx={{ fontSize: "0.875rem" }} />} component="h2">
          Critical Monitor — Compliant Alerts
        </SectionHeading>

        <NotificationFiltersBar filters={filters} onFiltersChange={onFiltersChange} />

        {status === "unavailable" && (
          <Alert severity="info">Notifications are unavailable for this deployment session.</Alert>
        )}
        {status === "loading" && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress aria-label="Loading notifications" />
          </Box>
        )}
        {status === "error" && (
          <Alert severity="error">{error?.message ?? "Unable to load notifications."}</Alert>
        )}
        {(status === "empty" || status === "ready") && (
          <NotificationList
            notifications={notifications}
            selectedNotificationId={selectedAlertId}
            onSelect={onSelectNotification}
          />
        )}
        {status === "unavailable" && (
          <Typography color="text.secondary">Check your runtime access and permissions.</Typography>
        )}
      </Box>
      {selectedAlertId !== null && (
        <AlertDetailsPanel alertId={selectedAlertId} onClose={onCloseAlert} />
      )}
    </Box>
  );
}
